import { BookOpen, Sparkles } from "lucide-react";
import { useMotionPreference } from "@/hooks/useMotionPreference";
import { useReadingMode } from "@/hooks/useReadingMode";
import type { MotionPref } from "@/lib/motionPreference";

const OPTIONS: { value: MotionPref; label: string }[] = [
  { value: "system", label: "System" },
  { value: "on", label: "Reduce" },
  { value: "off", label: "Full" },
];

/**
 * Accessibility controls letting visitors override the operating-system
 * "reduce motion" setting and switch on a calmer site-wide reading mode.
 * Lives in the footer so it is reachable site-wide.
 */
const MotionPreferenceToggle = () => {
  const { pref, setPref } = useMotionPreference();
  const { reading, setReading } = useReadingMode();

  return (
    <div>
      <h4 className="font-sans font-semibold text-[11px] uppercase tracking-[0.28em] mb-4 text-accent flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
        Reading &amp; Motion
      </h4>
      <p className="text-xs font-sans leading-relaxed text-[hsl(40_25%_82%)]/80 mb-4">
        Choose how much animation you see across the site. Your choice overrides
        your device settings and is remembered on this browser.
      </p>
      <div
        role="radiogroup"
        aria-label="Reduce motion preference"
        className="inline-flex rounded-full border border-[hsl(40_33%_94%)]/20 p-1"
      >
        {OPTIONS.map((opt) => {
          const active = pref === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setPref(opt.value)}
              className={`px-4 py-1.5 rounded-full font-sans text-[11px] uppercase tracking-[0.18em] transition-colors ${
                active
                  ? "bg-accent text-accent-foreground"
                  : "text-[hsl(40_25%_82%)]/80 hover:text-accent"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <p className="text-xs font-sans leading-relaxed text-[hsl(40_25%_82%)]/80 mt-5 mb-3">
        Reading mode simplifies typography and calms motion for long-form and
        archive pages.
      </p>
      <button
        type="button"
        role="switch"
        aria-checked={reading}
        aria-label="Reading mode"
        onClick={() => setReading(!reading)}
        className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border font-sans text-[11px] uppercase tracking-[0.18em] transition-colors ${
          reading
            ? "bg-accent text-accent-foreground border-accent"
            : "border-[hsl(40_33%_94%)]/20 text-[hsl(40_25%_82%)]/80 hover:text-accent"
        }`}
      >
        <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
        Reading mode {reading ? "on" : "off"}
      </button>
    </div>
  );
};

export default MotionPreferenceToggle;
